import React, { useState, useEffect } from 'react';
import { Node, Edge } from 'reactflow';
import { useGraphContext } from './GraphContext';

interface GraphSnapshot {
  id: string;
  timestamp: string;
  description: string;
  nodes: Node[];
  edges: Edge[];
}

interface GraphTimelineProps {
  sessionId: string;
  onSnapshotSelect: (snapshot: GraphSnapshot) => void;
}

export const GraphTimeline: React.FC<GraphTimelineProps> = ({
  sessionId,
  onSnapshotSelect
}) => {
  const [snapshots, setSnapshots] = useState<GraphSnapshot[]>([]);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { loadGraphSnapshots } = useGraphContext();
  
  // Load graph snapshots
  useEffect(() => {
    const fetchSnapshots = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const snapshots = await loadGraphSnapshots(sessionId);
        setSnapshots(snapshots);
        
        // Select the most recent snapshot by default
        if (snapshots.length > 0) {
          setSelectedSnapshotId(snapshots[snapshots.length - 1].id);
          onSnapshotSelect(snapshots[snapshots.length - 1]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load graph snapshots');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (sessionId) {
      fetchSnapshots();
    }
  }, [sessionId, loadGraphSnapshots, onSnapshotSelect]);
  
  // Handle snapshot selection
  const handleSnapshotSelect = (snapshotId: string) => {
    const snapshot = snapshots.find(s => s.id === snapshotId);
    if (snapshot) {
      setSelectedSnapshotId(snapshotId);
      onSnapshotSelect(snapshot);
    }
  };
  
  if (isLoading) {
    return <div className="text-center py-2">Loading timeline...</div>;
  }
  
  if (error) {
    return <div className="text-red-500 text-center py-2">Error: {error}</div>;
  }
  
  if (snapshots.length === 0) {
    return <div className="text-gray-500 text-center py-2">No timeline data available</div>;
  }
  
  return (
    <div className="graph-timeline bg-white rounded-lg shadow-lg p-4">
      <h3 className="font-medium mb-3">Graph Timeline</h3>
      
      <div className="relative pt-6 pb-2">
        {/* Timeline track */}
        <div className="absolute top-6 left-0 right-0 h-1 bg-gray-200"></div>
        
        {/* Timeline points */}
        <div className="flex justify-between relative">
          {snapshots.map((snapshot, index) => (
            <div 
              key={snapshot.id}
              className="flex flex-col items-center cursor-pointer"
              onClick={() => handleSnapshotSelect(snapshot.id)}
            >
              <div className="text-xs text-gray-500 mb-2 w-20 text-center truncate">
                {new Date(snapshot.timestamp).toLocaleTimeString()}
              </div>
              <div 
                className={`w-3 h-3 rounded-full z-10 ${
                  selectedSnapshotId === snapshot.id
                    ? 'bg-blue-600 ring-4 ring-blue-100'
                    : 'bg-gray-400'
                }`}
              ></div>
              <div className="text-xs text-gray-600 mt-2 w-20 text-center truncate">
                {snapshot.description || `Snapshot ${index + 1}`}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Playback controls */}
      <div className="flex justify-center mt-4 space-x-2">
        <button
          className="p-2 rounded-full bg-gray-100 hover:bg-gray-200"
          onClick={() => {
            const currentIndex = snapshots.findIndex(s => s.id === selectedSnapshotId);
            if (currentIndex > 0) {
              handleSnapshotSelect(snapshots[currentIndex - 1].id);
            }
          }}
          disabled={snapshots.findIndex(s => s.id === selectedSnapshotId) === 0}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </button>
        
        <button
          className="p-2 rounded-full bg-gray-100 hover:bg-gray-200"
          onClick={() => {
            const currentIndex = snapshots.findIndex(s => s.id === selectedSnapshotId);
            if (currentIndex < snapshots.length - 1) {
              handleSnapshotSelect(snapshots[currentIndex + 1].id);
            }
          }}
          disabled={snapshots.findIndex(s => s.id === selectedSnapshotId) === snapshots.length - 1}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};
